import React from 'react';
import {
  DIETARY_OPTIONS,
  SEN_STATUSES,
  SEN_CATEGORY_OPTIONS,
  EMPTY_SPECIAL_NEEDS,
  normalizeSpecialNeeds,
} from '../utils/childFields';

/**
 * SpecialNeedsAndDietPicker
 *
 * Drop-in form section for capturing structured Special Educational Needs
 * (SEND) info and dietary requirements for a child. Used in both the parent
 * Quick Add/Edit Child modal AND the Enrollment Wizard so the experience is
 * identical wherever a child record can be created or edited.
 *
 * Controlled via two slices of parent state:
 *   - dietary:        string[]                (e.g. ['Halal', 'Nut-free'])
 *   - specialNeeds:   SpecialNeeds object     (see utils/childFields.js)
 *
 * The parent owns the source of truth; this component only fires onChange
 * callbacks. That keeps it composable with the existing FormData-based
 * Quick Add modal AND the controlled-state Enrollment Wizard.
 */
export function SpecialNeedsAndDietPicker({
  dietary,
  onDietaryChange,
  specialNeeds,
  onSpecialNeedsChange,
  compact = false,
  testIdPrefix = '',
}) {
  const sn = normalizeSpecialNeeds(specialNeeds);

  const toggleDietary = (option) => {
    const next = dietary.includes(option)
      ? dietary.filter((d) => d !== option)
      : [...dietary, option];
    onDietaryChange(next);
  };

  const toggleCategory = (code) => {
    const cats = sn.categories.includes(code)
      ? sn.categories.filter((c) => c !== code)
      : [...sn.categories, code];
    onSpecialNeedsChange({ ...sn, categories: cats });
  };

  const updateField = (field, value) => {
    onSpecialNeedsChange({ ...sn, [field]: value });
  };

  // When SEN is toggled off we clear the rest so we never persist orphaned
  // categories / review dates against a child marked as "no SEN".
  const setHasSen = (on) => {
    if (!on) {
      onSpecialNeedsChange({ ...EMPTY_SPECIAL_NEEDS });
    } else {
      onSpecialNeedsChange({ ...sn, has_sen: true, sen_status: sn.sen_status === 'none' ? 'sen_support' : sn.sen_status });
    }
  };

  return (
    <div data-testid={`${testIdPrefix}sn-diet-picker`}>
      {/* ============================================================
          Dietary requirements — multi-select chips
         ============================================================ */}
      <div className="form-group">
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Dietary requirements
          <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', fontWeight: 400 }}>
            (select all that apply)
          </span>
        </label>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.4rem',
            marginTop: '0.35rem',
          }}
          data-testid={`${testIdPrefix}dietary-chips`}
        >
          {DIETARY_OPTIONS.map((opt) => {
            const selected = dietary.includes(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => toggleDietary(opt)}
                data-testid={`${testIdPrefix}dietary-chip-${opt.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                style={{
                  padding: '0.35rem 0.75rem',
                  borderRadius: '999px',
                  border: '1px solid',
                  borderColor: selected ? 'hsl(var(--primary))' : 'hsl(var(--border))',
                  background: selected ? 'hsl(var(--primary))' : 'transparent',
                  color: selected ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                  fontSize: '0.825rem',
                  cursor: 'pointer',
                  transition: 'background-color 150ms ease, color 150ms ease, border-color 150ms ease',
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>

      {/* ============================================================
          Special Educational Needs (SEND)
         ============================================================ */}
      <div
        className="form-group"
        style={{
          padding: compact ? '0.85rem' : '1rem',
          borderRadius: 'var(--radius)',
          border: '1px solid hsl(var(--border))',
          background: 'hsl(var(--muted) / 0.4)',
          marginTop: '0.5rem',
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
          <input
            type="checkbox"
            checked={!!sn.has_sen}
            onChange={(e) => setHasSen(e.target.checked)}
            data-testid={`${testIdPrefix}has-sen-toggle`}
          />
          <span style={{ fontWeight: 600 }}>This child has identified special educational needs (SEND)</span>
        </label>
        <p style={{ margin: 0, fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }}>
          Includes ADHD, autism, speech &amp; language, EHC Plans and more. Helps staff plan support and meet OFSTED expectations.
        </p>

        {sn.has_sen && (
          <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Status</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.3rem' }}>
                {SEN_STATUSES.map((s) => (
                  <label
                    key={s.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.4rem',
                      padding: '0.35rem 0.75rem',
                      borderRadius: '6px',
                      border: '1px solid',
                      borderColor: sn.sen_status === s.value ? 'hsl(var(--primary))' : 'hsl(var(--border))',
                      background: sn.sen_status === s.value ? 'hsl(var(--primary) / 0.1)' : 'transparent',
                      cursor: 'pointer',
                      fontSize: '0.825rem',
                    }}
                    data-testid={`${testIdPrefix}sen-status-${s.value}`}
                  >
                    <input
                      type="radio"
                      name={`${testIdPrefix}sen_status`}
                      checked={sn.sen_status === s.value}
                      onChange={() => updateField('sen_status', s.value)}
                      style={{ margin: 0 }}
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                Categories <span style={{ fontWeight: 400, color: 'hsl(var(--muted-foreground))' }}>(select all that apply)</span>
              </label>
              <div
                style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.3rem' }}
                data-testid={`${testIdPrefix}sen-category-chips`}
              >
                {SEN_CATEGORY_OPTIONS.map((c) => {
                  const selected = sn.categories.includes(c.code);
                  return (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => toggleCategory(c.code)}
                      data-testid={`${testIdPrefix}sen-cat-${c.code.toLowerCase()}`}
                      style={{
                        padding: '0.3rem 0.7rem',
                        borderRadius: '999px',
                        border: '1px solid',
                        borderColor: selected ? 'hsl(var(--primary))' : 'hsl(var(--border))',
                        background: selected ? 'hsl(var(--primary))' : 'transparent',
                        color: selected ? 'hsl(var(--primary-foreground))' : 'hsl(var(--foreground))',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                      }}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {sn.sen_status === 'ehcp' && (
              <div>
                <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>EHCP review date</label>
                <input
                  type="date"
                  value={sn.ehcp_review_date ? String(sn.ehcp_review_date).slice(0, 10) : ''}
                  onChange={(e) => updateField(
                    'ehcp_review_date',
                    e.target.value ? new Date(e.target.value).toISOString() : null,
                  )}
                  data-testid={`${testIdPrefix}ehcp-review-date`}
                  style={{ marginTop: '0.3rem', width: 'fit-content' }}
                />
                <small style={{ display: 'block', color: 'hsl(var(--muted-foreground))', marginTop: '0.2rem' }}>
                  EHCPs must be reviewed at least annually.
                </small>
              </div>
            )}

            <div>
              <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Notes for staff</label>
              <textarea
                rows={2}
                value={sn.notes || ''}
                onChange={(e) => updateField('notes', e.target.value)}
                placeholder="Strategies that work, sensory triggers, communication preferences…"
                data-testid={`${testIdPrefix}sen-notes`}
                style={{ width: '100%', marginTop: '0.3rem' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SpecialNeedsAndDietPicker;
